import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { PLACES_INPUT_MIN, type AddressSuggestion, type ResolvedAddress } from '../../../supabase/functions/_shared/placesProtocol';
import { resolveAddress, suggestAddresses } from '../../backend/placesClient';
import { useT } from '../../i18n/i18n';
import { Icon } from '../../ui/components';

// The address row of New Inspection (Owner 2026-09-14: 「跟谷歌地图一样，打几个字就可以出现多个地址
// 选择，选完自动填写」). Typing asks the places-v2 function for candidates after a short pause; one
// tap resolves the place and hands street / city / state / ZIP to the form. Everything else stays a
// plain text field: offline, not configured, not signed in, or an upstream failure simply shows no
// list and never blocks typing. One Google session token covers the typing burst and the pick.
export const ADDRESS_SUGGEST_DEBOUNCE_MS = 250;

function newSessionToken(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// "Plainview, NY, USA" → city Plainview, state NY (the fallback when Place Details cannot be read).
export function splitSecondaryText(secondary: string): { city: string; state: string } {
  const parts = secondary
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^(usa|united states)$/i.test(p));
  const state = parts.length >= 2 && /^[A-Z]{2}$/.test(parts[parts.length - 1]) ? parts[parts.length - 1] : '';
  const city = parts.length > 0 ? parts[0] : '';
  return { city, state };
}

// TestFlight 211 (2026-10-28): "100 Old Country Rd, Hicksville" resolved to Google's place for the STREET, whose
// address is "Old Country Rd" — the house number the inspector picked was gone. When the picked candidate starts
// with a house number and the resolved street does not, the candidate's own text is the street.
export function keepPickedHouseNumber(resolved: ResolvedAddress, mainText: string): ResolvedAddress {
  const number = /^(\d+[A-Za-z]?(?:-\d+)?)\s/.exec(mainText.trim())?.[1];
  if (!number || resolved.address.trim().startsWith(number)) return resolved;
  return { ...resolved, address: mainText.trim() };
}

export function AddressRow(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onResolved: (address: ResolvedAddress) => void;
  placeholder?: string;
  error?: boolean;
  testId: string;
}) {
  const { t } = useT();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const session = useRef(newSessionToken());
  const seq = useRef(0);
  // Only what the inspector TYPES asks for candidates; a resolved pick sets the value without asking again.
  const typed = useRef(false);
  // Left the field since the last keystroke: an answer that lands afterwards is kept for the next focus but
  // does not pop the list open over the rows below.
  const left = useRef(false);
  const { value } = props;

  useEffect(() => {
    if (!typed.current) return undefined;
    const input = value.trim();
    if (input.length < PLACES_INPUT_MIN) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      void suggestAddresses(input, session.current).then((outcome) => {
        if (mine !== seq.current) return; // a newer keystroke owns the list
        if (outcome.kind === 'ok' && outcome.value.length > 0) {
          setSuggestions(outcome.value);
          setOpen(!left.current);
        } else {
          setSuggestions([]);
          setOpen(false);
        }
      });
    }, ADDRESS_SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  async function choose(suggestion: AddressSuggestion) {
    seq.current += 1;
    typed.current = false;
    setOpen(false);
    setSuggestions([]);
    setBusy(true);
    const token = session.current;
    // The details call ends the Google session; the next typing burst starts a new one.
    session.current = newSessionToken();
    try {
      const outcome = await resolveAddress(suggestion.placeId, token);
      if (outcome.kind === 'ok') props.onResolved(keepPickedHouseNumber(outcome.value, suggestion.mainText));
      else {
        const { city, state } = splitSecondaryText(suggestion.secondaryText);
        props.onResolved({ address: suggestion.mainText, city, state, zip: '', formatted: [suggestion.mainText, suggestion.secondaryText].filter((s) => s.length > 0).join(', ') });
      }
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  const listId = `${props.id}-suggestions`;
  return (
    <div className="address-row-wrap" data-testid={`${props.testId}-wrap`} data-suggesting={open ? 'true' : 'false'}>
      <label className={`form-row form-row-input ${props.error ? 'form-row-error' : ''}`} htmlFor={props.id}>
        <span className="form-row-label">{props.label}</span>
        <input
          id={props.id}
          className="form-row-field"
          type="text"
          value={value}
          placeholder={props.placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          onChange={(event) => {
            typed.current = true;
            left.current = false;
            props.onChange(event.target.value);
          }}
          onFocus={() => {
            left.current = false;
            if (suggestions.length > 0) setOpen(true);
          }}
          // Leaving the field folds the list away; a tap on a candidate keeps the focus (its mousedown is
          // prevented below), so the tap lands before the list can close.
          onBlur={() => {
            left.current = true;
            setOpen(false);
          }}
          onKeyDown={onKeyDown}
          data-testid={props.testId}
        />
      </label>
      {open && suggestions.length > 0 ? (
        <div className="address-suggestions" id={listId} role="listbox" aria-label={t('address.suggestions')} data-testid={`${props.testId}-suggestions`}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              type="button"
              role="option"
              aria-selected={false}
              className="address-suggestion"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void choose(suggestion)}
              disabled={busy}
              data-testid={`${props.testId}-suggestion`}
              data-place-id={suggestion.placeId}
            >
              <Icon name="home_work" size={20} />
              <span className="address-suggestion-main">
                <span className="address-suggestion-title">{suggestion.mainText}</span>
                {suggestion.secondaryText.length > 0 ? <span className="address-suggestion-sub">{suggestion.secondaryText}</span> : null}
              </span>
            </button>
          ))}
          <p className="address-attribution">{t('address.poweredBy')}</p>
        </div>
      ) : null}
    </div>
  );
}
